import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ClipboardList, Receipt, Banknote, AlertTriangle, FileSignature, ReceiptText } from "lucide-react";
import DaFatturareTab from "./DaFatturareTab";
import FattureTab from "./FattureTab";
import RegistroIncassiTab from "./RegistroIncassiTab"; // legacy view (kept available)
import PrimaNotaTab from "./PrimaNotaTab";
import SollecitiTab from "./SollecitiTab";
import { SectionContextBar } from "@/components/suite";
import api from "@/api";

/**
 * Fatturazione Tab Component
 * Unified view: Da Fatturare + Fatture Emesse + Registro Incassi + Solleciti
 * 
 * Role-based sub-tab access:
 *   - operatore_studio: Da Fatturare, Fatture Emesse only
 *   - operatore_agenzia: all 4 sub-tabs
 *   - amministratore: all 4 sub-tabs
 */
function FatturazioneTab({
  // DaFatturare props
  clients,
  casesToBill,
  readyToBill,
  missingBillingData,
  totalToBill,
  billingSearchQuery,
  setBillingSearchQuery,
  billingFilterClient,
  setBillingFilterClient,
  billingFilterPriority,
  setBillingFilterPriority,
  billingFilterStatus,
  setBillingFilterStatus,
  handleEditCase,
  createInvoiceFromCase,
  // Fatture props
  invoices,
  cases,
  updateInvoiceStatus,
  exportInvoiceXML,
  onAddPayment,
  onRemovePayment,
  refreshInvoices,
  onEditInvoice,
  onDuplicateInvoice,
  onCreateInvoice,
  onDeleteInvoice,
  settings,
  firstInvoiceLabel,
  currentOperator,
  // 28/02/2026 — Prima Nota: serve operators per le select (clients è già nei props sopra)
  operators = [],
  // Role
  operatorRole = "amministratore",
  // Default filter
  defaultManagementFilter = "agenzia",
  // 28/02/2026 — callback per aprire l'`InvoiceDialog` in modalità
  // CREATE Documento Proforma. Inietta dal parent (App.js → hook
  // useInvoices.openNewProforma).
  openNewProforma,
  // P2 (04/06/2026) — Inoltrate a PrimaNotaTab per consentire inline
  // creation cliente nel dialog "+ Registra incasso".
  onOpenNewClient,
  isClientDialogOpen,
  setIsClientDialogOpen,
  setClientForm,
  resetClientForm,
}) {
  // Default sub-tab → "Fatture Emesse" (was "da-fatturare" before)
  const [activeSubTab, setActiveSubTab] = useState("fatture");

  // ── PROFORMA STATE (28/02/2026): gemello non-fiscale delle fatture.
  // Lista caricata separatamente via documentType=proforma — esclusa
  // dai registri ufficiali ma riusa lo stesso `FattureTab` per
  // rendering, filtri, ricerca, PDF, email, pagamenti, ecc.
  const [proformas, setProformas] = useState([]);
  const [proformasLoading, setProformasLoading] = useState(false);

  const refreshProformas = useCallback(async () => {
    setProformasLoading(true);
    try {
      const data = await api.getProformas();
      setProformas(Array.isArray(data) ? data : []);
    } catch (err) {
      console.warn("getProformas failed:", err?.message || err);
      setProformas([]);
    } finally {
      setProformasLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeSubTab === "proforma") refreshProformas();
  }, [activeSubTab, refreshProformas]);

  // 27/02/2026 — Listener globale: dopo che il dialog Fattura/Proforma
  // salva una proforma (sia da "+ Nuova Proforma" sia da "Da Fatturare"
  // → Crea Documento Proforma), ricarichiamo la lista anche se il
  // sub-tab era già "proforma".
  useEffect(() => {
    const onProformaSaved = () => { refreshProformas(); };
    window.addEventListener("gi:proforma-saved", onProformaSaved);
    return () => window.removeEventListener("gi:proforma-saved", onProformaSaved);
  }, [refreshProformas]);

  // Role-based access
  const isStudio = operatorRole === "operatore_studio";
  const canSeeIncassi = operatorRole !== "operatore_studio";
  const canSeeSolleciti = operatorRole !== "operatore_studio";
  // Tutti i ruoli che possono emettere fatture possono emettere proforma.
  const canSeeProforma = true;

  // Default management filter based on role
  const roleDefaultFilter = isStudio ? "studio"
    : operatorRole === "operatore_agenzia" ? "agenzia"
    : defaultManagementFilter;

  // ── KPI sintetici per <SectionContextBar /> (v1.11.0 UX v3 PoC)
  // Calcolati inline dai dati già presenti nelle props: nessuna nuova
  // fetch. La logica di dominio (cosa è "da incassare" / "sollecito")
  // resta identica a quella usata dalle sub-tabs — qui la sintetizziamo
  // in numeri high-level da mostrare nella fascia contestuale.
  const contextKpis = useMemo(() => {
    const totalInvoices = Array.isArray(invoices) ? invoices.length : 0;
    const toBill = Array.isArray(casesToBill) ? casesToBill.length : 0;

    // "Da incassare" = fatture emesse non in stato "pagata"
    // (accettiamo diverse varianti di label per robustezza).
    const isPaid = (inv) => {
      const s = String(inv?.status || "").toLowerCase();
      return s === "pagata" || s === "paid" || s === "saldata";
    };
    const unpaid = Array.isArray(invoices) ? invoices.filter((i) => !isPaid(i)) : [];
    const unpaidCount = unpaid.length;

    // "Solleciti urgenti" = fatture non pagate scadute da > 15gg
    const today = new Date();
    const overdueUrgent = unpaid.filter((inv) => {
      const dueRaw = inv?.dueDate || inv?.expiryDate || inv?.scadenza;
      if (!dueRaw) return false;
      const d = new Date(dueRaw);
      if (Number.isNaN(d.getTime())) return false;
      const daysOverdue = Math.floor((today - d) / (1000 * 60 * 60 * 24));
      return daysOverdue > 15;
    }).length;

    return [
      {
        value: totalInvoices,
        label: "fatture emesse",
        tone: "default",
        testid: "fatt-ctx-kpi-emesse",
        onClick: () => setActiveSubTab("fatture"),
      },
      {
        value: toBill,
        label: "da fatturare",
        tone: toBill > 0 ? "info" : "default",
        testid: "fatt-ctx-kpi-da-fatturare",
        onClick: () => setActiveSubTab("da-fatturare"),
      },
      {
        value: unpaidCount,
        label: "da incassare",
        tone: unpaidCount > 0 ? "warning" : "default",
        testid: "fatt-ctx-kpi-da-incassare",
        onClick: () => setActiveSubTab("fatture"),
      },
      {
        value: overdueUrgent,
        label: "solleciti urgenti",
        tone: overdueUrgent > 0 ? "danger" : "default",
        testid: "fatt-ctx-kpi-solleciti",
        onClick: () => setActiveSubTab("solleciti"),
      },
    ];
  }, [invoices, casesToBill]);

  const criticalNotice = useMemo(() => {
    const dangerKpi = contextKpis.find((k) => k.tone === "danger" && Number(k.value) > 0);
    if (!dangerKpi) return null;
    return {
      message: `${dangerKpi.value} fatture in ritardo grave`,
      onClick: () => setActiveSubTab("solleciti"),
    };
  }, [contextKpis]);

  return (
    <div className="space-y-4">
      {/* ── Fascia contestuale evoluta (UX v3) ─────────────
          Sostituisce l'ex "kpiStrip" con una superficie unica
          compatta 64/72px che integra titolo + descrizione +
          KPI inline + eventuale stato critico + azione primaria. */}
      <SectionContextBar
        icon={ReceiptText}
        title="Fatturazione"
        description="Fatture emesse, proforma, incassi e solleciti"
        kpis={contextKpis}
        criticalNotice={criticalNotice}
        testid="fatturazione-context-bar"
      />

      <Tabs value={activeSubTab} onValueChange={setActiveSubTab} className="w-full">
        {/* Sub-tabs come chip di navigazione elegante (UX v3 · 26/07/2026)
            - Testo con separatori pipe visivi
            - Attivo in verde brand + underline sottile (no fill)
            - No box shadow, altezza ridotta, bordo bottom guida
            - Accompagna la ContextBar senza competere */}
        <TabsList
          className="suite-subnav w-auto h-auto p-0 bg-transparent rounded-none gap-0 justify-start inline-flex"
          style={{ borderBottom: "1px solid var(--suite-border)" }}
        >
          <TabsTrigger
            value="da-fatturare"
            className="suite-subnav-item data-[state=active]:bg-transparent data-[state=active]:text-[var(--gis-green)] data-[state=active]:shadow-none rounded-none border-0 relative"
          >
            <ClipboardList className="h-3.5 w-3.5 mr-1.5" />
            Da Fatturare
          </TabsTrigger>
          <TabsTrigger
            value="fatture"
            className="suite-subnav-item data-[state=active]:bg-transparent data-[state=active]:text-[var(--gis-green)] data-[state=active]:shadow-none rounded-none border-0 relative"
          >
            <Receipt className="h-3.5 w-3.5 mr-1.5" />
            Fatture Emesse
          </TabsTrigger>
          {canSeeProforma && (
            <TabsTrigger
              value="proforma"
              className="suite-subnav-item data-[state=active]:bg-transparent data-[state=active]:text-[var(--gis-green)] data-[state=active]:shadow-none rounded-none border-0 relative"
              data-testid="proforma-tab"
            >
              <FileSignature className="h-3.5 w-3.5 mr-1.5" />
              Proforma
            </TabsTrigger>
          )}
          {canSeeIncassi && (
            <TabsTrigger
              value="incassi"
              className="suite-subnav-item data-[state=active]:bg-transparent data-[state=active]:text-[var(--gis-green)] data-[state=active]:shadow-none rounded-none border-0 relative"
              data-testid="registro-incassi-tab"
            >
              <Banknote className="h-3.5 w-3.5 mr-1.5" />
              Registro Incassi
            </TabsTrigger>
          )}
          {canSeeSolleciti && (
            <TabsTrigger
              value="solleciti"
              className="suite-subnav-item data-[state=active]:bg-transparent data-[state=active]:text-[var(--gis-green)] data-[state=active]:shadow-none rounded-none border-0 relative"
              data-testid="solleciti-tab"
            >
              <AlertTriangle className="h-3.5 w-3.5 mr-1.5" />
              Solleciti
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="da-fatturare" className="mt-4">
          <DaFatturareTab
            clients={clients}
            casesToBill={casesToBill}
            readyToBill={readyToBill}
            missingBillingData={missingBillingData}
            totalToBill={totalToBill}
            searchQuery={billingSearchQuery}
            setSearchQuery={setBillingSearchQuery}
            filterClient={billingFilterClient}
            setFilterClient={setBillingFilterClient}
            filterPriority={billingFilterPriority}
            setFilterPriority={setBillingFilterPriority}
            filterBillingStatus={billingFilterStatus}
            setFilterBillingStatus={setBillingFilterStatus}
            handleEditCase={handleEditCase}
            createInvoiceFromCase={(caseItem, documentType = "invoice") => {
              createInvoiceFromCase(caseItem, documentType);
              setActiveSubTab(documentType === "proforma" ? "proforma" : "fatture");
            }}
            defaultManagementFilter={roleDefaultFilter}
          />
        </TabsContent>

        <TabsContent value="fatture" className="mt-4">
          <FattureTab
            invoices={invoices}
            clients={clients}
            cases={cases}
            updateInvoiceStatus={updateInvoiceStatus}
            exportInvoiceXML={exportInvoiceXML}
            onAddPayment={onAddPayment}
            onRemovePayment={onRemovePayment}
            refreshInvoices={refreshInvoices}
            onEditInvoice={onEditInvoice}
            onDuplicateInvoice={onDuplicateInvoice}
            onDeleteInvoice={onDeleteInvoice}
            settings={settings}
            firstInvoiceLabel={firstInvoiceLabel}
            defaultManagementFilter={roleDefaultFilter}
            onCreateInvoice={onCreateInvoice}
            operatorRole={operatorRole}
          />
        </TabsContent>

        {canSeeProforma && (
          <TabsContent value="proforma" className="mt-4">
            {/* Stesso `FattureTab`, riusato come motore di rendering. Il
                prop `documentType="proforma"` cambia titoli, label e
                payload del dialog di creazione/modifica. La lista è
                caricata separatamente via `proformas`. */}
            <FattureTab
              invoices={proformas}
              clients={clients}
              cases={cases}
              updateInvoiceStatus={updateInvoiceStatus}
              exportInvoiceXML={exportInvoiceXML}
              onAddPayment={onAddPayment}
              onRemovePayment={onRemovePayment}
              // BUG FIX 04/06/2026 — La conversione Proforma → Fattura
              // CREA un documento `invoice` che deve apparire subito in
              // "Fatture Emesse". Refreshare SOLO `proformas` lasciava la
              // nuova fattura invisibile finché non si ricaricava la
              // pagina (gap apparente nel registro: 176P → 178P, 177P
              // esistente in DB ma non listato). Refreshamo entrambe.
              refreshInvoices={async () => {
                if (typeof refreshProformas === "function") {
                  try { await refreshProformas(); } catch { /* swallow */ }
                }
                if (typeof refreshInvoices === "function") {
                  try { await refreshInvoices(); } catch { /* swallow */ }
                }
              }}
              onEditInvoice={onEditInvoice}
              onDeleteInvoice={onDeleteInvoice}
              settings={settings}
              firstInvoiceLabel={firstInvoiceLabel}
              defaultManagementFilter={roleDefaultFilter}
              documentType="proforma"
              isLoadingExternally={proformasLoading}
              onCreateProforma={openNewProforma}
              // 07/06/2026 — Dopo conversione: switch al tab "Fatture
              // Emesse" + apertura immediata della nuova fattura in edit
              // mode. L'operatore puo' completare pagamento/note/riferimenti
              // senza dover cercare la fattura manualmente.
              onConversionSuccess={(newInvoice) => {
                setActiveSubTab("fatture");
                if (typeof onEditInvoice === "function" && newInvoice) {
                  onEditInvoice(newInvoice);
                }
              }}
            />
          </TabsContent>
        )}

        {canSeeIncassi && (
          <TabsContent value="incassi" className="mt-4">
            {/* 28/02/2026 — Prima Nota Step 2: nuova vista unificata
                (entrate documentali virtuali + manuali + uscite).
                Mantiene compatibilità: data-testid invariato. */}
            <PrimaNotaTab
              clients={clients}
              cases={cases}
              operators={operators}
              currentOperator={currentOperator}
              onOpenNewClient={onOpenNewClient}
              isClientDialogOpen={isClientDialogOpen}
              setIsClientDialogOpen={setIsClientDialogOpen}
              setClientForm={setClientForm}
              resetClientForm={resetClientForm}
              operatorRole={operatorRole}
            />
            {/* Legacy RegistroIncassiTab disponibile in archivio.
                Reintroducibile sotto a richiesta dell'ufficio durante
                il test. */}
            {false && (
              <RegistroIncassiTab invoices={invoices} clients={clients} cases={cases} />
            )}
          </TabsContent>
        )}

        {canSeeSolleciti && (
          <TabsContent value="solleciti" className="mt-4">
            <SollecitiTab currentOperator={currentOperator} />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

export default React.memo(FatturazioneTab);

